import { uploadFile } from '@/lib/upload-file';
import type { PurchaseDocumentPayload } from './types';

export const DOCUMENT_ACCEPT = 'application/pdf,image/jpeg,image/png,image/webp';
export const DOCUMENT_MAX_BYTES = 10 * 1024 * 1024;

/**
 * Upload a vendor invoice / challan through the presigned-URL flow and return
 * the payload the API stores (the S3 *key*, not a URL — the API resolves it to
 * an authenticated /uploads/asset link on read, so private files stay private).
 *
 * Throws DocumentUploadError on any failure so callers stop the save instead of
 * silently recording a receipt/bill without its document.
 */
export class DocumentUploadError extends Error {
  constructor(message = 'Document upload failed') {
    super(message);
    this.name = 'DocumentUploadError';
  }
}

export async function uploadPurchaseDocument(
  file: File,
  fetchImpl: typeof fetch = fetch,
): Promise<PurchaseDocumentPayload> {
  // One request: the API magic-byte checks the bytes BEFORE storing them (the
  // old flow let a renamed .exe land in S3 and deleted it afterwards), and the
  // size recorded is measured server-side rather than reported by the browser.
  let uploaded;
  try {
    uploaded = await uploadFile(file, 'document', fetchImpl);
  } catch (err) {
    throw new DocumentUploadError((err as Error).message || 'Upload failed');
  }

  return {
    file_key: uploaded.key,
    file_name: file.name,
    content_type: uploaded.content_type,
    size_bytes: uploaded.size_bytes,
  };
}

export function formatBytes(n: number | null | undefined): string {
  if (n == null) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
